import { Box, Button, Grid, Typography } from "@mui/material";
import { useEffect, useState, useContext, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FiltroMeses } from "./chartsLogic/filterMonth";
import { TarjetaLogrado } from "./chartsLogic/charts";
import { obtenerDiferencias, obtenerLogrado, obtenerDatosTabla } from "./chartsLogic/datacharts";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { lightBlue, deepPurple, teal, red } from "@mui/material/colors";
import { formatYm } from "./chartsLogic/utils";

export const SECTION_LABELS = (t) => ({
	Diurno: t("shifts.diurno"),
	Nocturno: t("shifts.nocturno"),
	Madrugada: t("shifts.madrugada"),
});

const baseColors = {
	Diurno: teal[200],
	Nocturno: lightBlue[200],
	Madrugada: deepPurple[200],
};

const DIF_CLASS = "col-diferencia";

const obtenerEstilosFila = (seccion) => ({
	backgroundColor: () =>
		seccion === "Diurno" ? teal[100] : seccion === "Nocturno" ? lightBlue[100] : deepPurple[100],
	color: "black",
	"&:hover": {
		backgroundColor: () =>
			seccion === "Diurno" ? teal[200] : seccion === "Nocturno" ? lightBlue[200] : deepPurple[200],
	},
});
const GridDif = ({ data = [], loading = false }) => {
	const { t } = useTranslation();
	const labels = SECTION_LABELS(t);
	const [mesesSeleccionados, setMesesSeleccionados] = useState([]);
	const [mesesFiltrados, setMesesFiltrados] = useState([]);
	const [diferencias, setDiferencias] = useState({ Diurno: 0, Nocturno: 0, Madrugada: 0 });
	const [logrados, setLogrados] = useState({ Diurno: 0, Nocturno: 0, Madrugada: 0 });
	const [datosTabla, setDatosTabla] = useState([]);
	const [tarjetaSeleccionada, setTarjetaSeleccionada] = useState(null);
	const optionsLoading = loading;

	useEffect(() => {
		const outputMonthly = data?.[0]?.output_planning_monthly || [];
		if (!outputMonthly.length) {
			setMesesFiltrados([]);
			setMesesSeleccionados([]);
			return;
		}

		const mesesUnicos = [
			...new Set(outputMonthly.map((i) => `${i.anno}-${String(i.mes).padStart(2, "0")}`)),
		];
		const mesesI18n = mesesUnicos.map((ym) => ({
			value: ym,
			label: formatYm(ym, t, { short: true }),
		}));

		setMesesFiltrados(mesesI18n);
		setMesesSeleccionados(mesesI18n.map((m) => m.value));
	}, [data, t]);

	const columns = useMemo(
		() => [
			{ field: "nombre", headerName: t("new_charts.name_contract"), width: 200 },
			{ field: "kronos_name", headerName: t("new_charts.kronos_name"), width: 150 },
			{
				field: "mes",
				headerName: t("new_charts.month"),
				flex: 1,
				renderCell: (params) => {
					const ym = params.value;
					if (!ym) return <span>–</span>;
					const [year] = ym.split("-");
					const code = formatYm(ym, t, { short: true }).split("-")[0];
					return <span>{`${code}-${year}`}</span>;
				},
			},
			{ field: "contratados_ne", headerName: t("new_charts.initial_real"), flex: 1 },
			{ field: "contratados_sp", headerName: t("new_charts.objetive"), flex: 1 },
			{
				field: "diferencia",
				headerName: t("new_charts.diferent"),
				flex: 1,
				cellClassName: DIF_CLASS,
				renderCell: (params) => {
					const v = parseFloat(params.value);
					const isFaltante = !isNaN(v) && v < 0;
					return (
						<strong
							style={{
								color: isFaltante ? red[600] : "black",
								fontWeight: isFaltante ? 700 : 400,
							}}
						>
							{params.value}
						</strong>
					);
				},
			},
			{ field: "zcero", headerName: t("new_charts.final_real"), flex: 1 },
			{ field: "logrado", headerName: t("new_charts.achieved"), flex: 1 },
		],
		[t],
	);

	const manejarSeleccionTodos = () => {
		if (mesesSeleccionados.length === mesesFiltrados.length) {
			setMesesSeleccionados([]);
		} else {
			setMesesSeleccionados(mesesFiltrados.map((mes) => mes.value));
		}
	};

	const manejarSeleccionTarjeta = (seccion) => {
		setTarjetaSeleccionada((prev) => (prev === seccion ? null : seccion));
	};

	useEffect(() => {
		if (data.length > 0 && mesesSeleccionados.length > 0) {
			const nuevasDiferencias = obtenerDiferencias(data, mesesSeleccionados);
			const nuevosLogrados = obtenerLogrado(data, mesesSeleccionados);
			let nuevosDatosTabla = obtenerDatosTabla(data, mesesSeleccionados);

			const secciones = ["Diurno", "Nocturno", "Madrugada"];

			const diferenciasConDefaults = secciones.reduce((acc, seccion) => {
				acc[seccion] = nuevasDiferencias[seccion] ?? null;
				return acc;
			}, {});

			const logradoConDefaults = secciones.reduce((acc, seccion) => {
				acc[seccion] = nuevosLogrados[seccion] ?? null;
				return acc;
			}, {});

			if (JSON.stringify(diferencias) !== JSON.stringify(diferenciasConDefaults)) {
				setDiferencias(diferenciasConDefaults);
			}
			if (JSON.stringify(logrados) !== JSON.stringify(logradoConDefaults)) {
				setLogrados(logradoConDefaults);
			}

			if (tarjetaSeleccionada) {
				nuevosDatosTabla = nuevosDatosTabla.filter((row) => row.seccion.includes(tarjetaSeleccionada));
			}
			setDatosTabla(nuevosDatosTabla);
		}
	}, [data, mesesSeleccionados, tarjetaSeleccionada]); // <- evita loops por diferencias/logrados

	return (
		<Box
			sx={{
				width: "100%",
				display: "flex",
				justifyContent: "center",
			}}
		>
			{/* Contenedor estilo popup: ancho controlado + padding + “aire” */}
			<Box
				sx={{
					width: "80%",
					px: 2,
					py: 3,
				}}
			>
				{/* Título */}
				<Typography gutterBottom variant="h5" sx={{ textAlign: "center", mb: 2 }}>
					{t("new_charts.title")}
				</Typography>

				{/* Layout general: aire entre secciones */}
				<Grid container rowSpacing={3} columnSpacing={3} alignItems="stretch">
					{/* Filtro */}
					<Grid item xs={12} md={9}>
						<FiltroMeses
							mesesSeleccionados={mesesSeleccionados}
							setMesesSeleccionados={setMesesSeleccionados}
							mesesFiltrados={mesesFiltrados}
							optionsLoading={optionsLoading}
						/>
					</Grid>

					{/* Botón select all */}
					<Grid item xs={12} md={3} sx={{ display: "flex", alignItems: "stretch" }}>
						<Button fullWidth variant="outlined" onClick={manejarSeleccionTodos} sx={{ borderRadius: 3 }}>
							{mesesSeleccionados.length === mesesFiltrados.length
								? t("new_charts.unselect_all")
								: t("new_charts.select_all")}
						</Button>
					</Grid>

					{/* KPI Cards */}
					{["Diurno", "Nocturno", "Madrugada"].map((seccion) => {
						const selected = tarjetaSeleccionada === seccion;

						return (
							<Grid item xs={12} md={4} key={seccion}>
								{/* Wrapper más grande que la card: crea el “halo” */}
								<Box
									role="button"
									tabIndex={0}
									onClick={() => manejarSeleccionTarjeta(seccion)}
									onKeyDown={(e) => {
										if (e.key === "Enter" || e.key === " ") manejarSeleccionTarjeta(seccion);
									}}
									sx={{
										width: "100%",
										height: "100%",
										cursor: "pointer",

										// ✅ importante para que el halo respete el radius
										borderRadius: 3,
										overflow: "hidden",

										// ✅ halo SOLO arriba (altura del halo)
										pt: 1.2,
										px: 0,
										pb: 0,

										// ✅ sin borde
										border: "none",

										// ✅ el color solo aparece cuando está seleccionado
										backgroundColor: tarjetaSeleccionada === seccion ? baseColors[seccion] : "transparent",

										transition: "background-color 0.2s ease",
										"&:hover": {
											backgroundColor: tarjetaSeleccionada === seccion ? baseColors[seccion] : "grey.100",
										},

										"&:focus-visible": {
											outline: "2px solid",
											outlineColor: "text.primary",
											outlineOffset: 2,
										},
									}}
								>
									{/* Card interna SIN modificar TarjetaLogrado */}
									<TarjetaLogrado
										titulo={labels[seccion]}
										diferencia={
											diferencias[seccion] !== undefined && diferencias[seccion] !== null
												? diferencias[seccion]
												: t("new_charts.no_data")
										}
										logrado={
											logrados[seccion] !== undefined && logrados[seccion] !== null
												? logrados[seccion]
												: t("new_charts.no_data")
										}
										seccion={seccion}
										cargando={optionsLoading}
										t={t}
									/>
								</Box>
							</Grid>
						);
					})}

					{/* Tabla */}
					<Grid item xs={12} sx={{ mt: 1 }}>
						{/* Wrapper tipo popup para separar visualmente */}
						<Box
							sx={{
								borderRadius: 3,
								border: "1px solid",
								borderColor: "divider",
								p: 1,
							}}
						>
							<Box sx={{ height: 420, width: "100%" }}>
								<DataGrid
									rows={datosTabla}
									columns={columns}
									pageSize={5}
									rowsPerPageOptions={[5, 10]}
									components={{ Toolbar: GridToolbar }}
									loading={loading}
									getRowClassName={(params) =>
										tarjetaSeleccionada && params.row.seccion.includes(tarjetaSeleccionada)
											? "fila-resaltada"
											: ""
									}
									sx={{
										// aire visual extra
										border: "none",
										"& .MuiDataGrid-columnHeaders": {
											borderRadius: 2,
										},
										"& .fila-resaltada": obtenerEstilosFila(tarjetaSeleccionada),
										"& .col-diferencia": {
											fontWeight: 600,
										},
									}}
								/>
							</Box>
						</Box>
					</Grid>
				</Grid>
			</Box>
		</Box>
	);
};

export { GridDif };
